import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { logger } from '../../helpers/logger';

/**
 * Component that mounts all child components in a React
 * portal. This is typically used to allow child components
 * to visually "break out" of their parent container.
 * @see https://reactjs.org/docs/portals.html
 */
export const Portal = ({ children, portalId = 'portal-root', shouldEmptyHtml = true }) => {
    const getAvailableContainer = (n) => {
        const availableContainers = document.getElementsByClassName(portalId);
        const potentialId = `${portalId}-${n}`;
        const potentialContainer = document.getElementById(potentialId);

        if (!potentialContainer || (shouldEmptyHtml && potentialContainer.innerHTML !== '')) {
            if (n < availableContainers?.length) {
                return getAvailableContainer(n + 1);
            } else {
                logger.error(
                    'React Portal limit reached. Add more portals to index.html or change the UI.',
                );
                return null;
            }
        }

        return potentialContainer;
    };

    const portalContainer = getAvailableContainer(1);

    const [innerHtmlEmptied, setInnerHtmlEmptied] = useState(false);

    // empties the target DOM node to allow a re-rendering when props change
    useEffect(() => {
        if (innerHtmlEmptied || !portalContainer || !shouldEmptyHtml) return;
        portalContainer.innerHTML = '';
        setInnerHtmlEmptied(true);
    }, [innerHtmlEmptied]);

    if (!innerHtmlEmptied && shouldEmptyHtml) return null;

    // only create portal if container is found
    // fix for "Target container is not a DOM element." error
    return !!portalContainer ? ReactDOM.createPortal(children, portalContainer) : null;
};

Portal.propTypes = {
    portalId: PropTypes.string,
    shouldEmptyHtml: PropTypes.bool,
};
